import { clamp } from '../utils';
import { ValueType, PointType, PositionType } from '../types';

type ObjectType = { [key: string]: any };

/**
 * Convert a point into a percentage value
 */
export function getPercentageFromPosition(position: PointType, clientRect: ObjectType): number {
  const length = clientRect.width;
  const sizePerc = position.x / length;

  return sizePerc || 0;
}

/**
 * Convert a point into a model value
 */
export function getValueFromPosition(position: PointType, minValue: number, maxValue: number, clientRect: ObjectType) {
  const sizePerc = getPercentageFromPosition(position, clientRect);
  const valueDiff = maxValue - minValue;

  return minValue + (valueDiff * sizePerc);
}

/**
 * Convert props into a range value
 */
export function getValueFromProps(props: ObjectType, isMultiValue: boolean): ValueType {
  if (isMultiValue) {
    return { ...props.value };
  }

  return {
    min: props.minValue,
    max: props.value,
  };
}

/**
 * Convert a model value into a percentage value
 */
export function getPercentageFromValue(value: number, minValue: number, maxValue: number): number {
  const validValue = clamp(value, minValue, maxValue);
  const valueDiff = maxValue - minValue;
  const valuePerc = (validValue - minValue) / valueDiff;

  return valuePerc || 0;
}

/**
 * Convert model values into percentage values
 */
export function getPercentagesFromValues(values: ValueType, minValue: number, maxValue: number): ValueType {
  return {
    min: getPercentageFromValue(values.min, minValue, maxValue),
    max: getPercentageFromValue(values.max, minValue, maxValue),
  };
}

/**
 * Convert a value into a point
 */
// eslint-disable-next-line @stylistic/max-len
export function getPositionFromValue(value: number, minValue: number, maxValue: number, clientRect: ObjectType): PointType {
  const length = clientRect.width;
  const valuePerc = getPercentageFromValue(value, minValue, maxValue);
  const positionValue = valuePerc * length;

  return {
    x: positionValue,
    y: 0,
  };
}

/**
 * Convert a range of values into points
 */
// eslint-disable-next-line @stylistic/max-len
export function getPositionsFromValues(values: ValueType, minValue: number, maxValue: number, clientRect: ObjectType): PositionType {
  return {
    min: getPositionFromValue(values.min, minValue, maxValue, clientRect),
    max: getPositionFromValue(values.max, minValue, maxValue, clientRect),
  };
}

/**
 * Convert an event into a point
 */
export function getPositionFromEvent(event: ObjectType, clientRect: ObjectType) {
  const length = clientRect.width;
  const { clientX } = event.touches ? event.touches[0] : event;

  return {
    x: clamp(clientX - clientRect.left, 0, length),
    y: 0,
  };
}

/**
 * Convert a value into a step value
 */
export function getStepValueFromValue(value: number, valuePerStep: number): number {
  return Math.round(value / valuePerStep) * valuePerStep;
}
